/* global Intl */
/**
 * Pure calendar utilities for the date picker. No DOM, no logging.
 *
 * `getMonthGrid` produces the 6×7 day grid for one month, with leading and
 * trailing days from neighboring months filled in (so the grid is always
 * exactly 42 cells regardless of where the month starts or ends).
 *
 * `getMonthName` and `getWeekdayHeaders` use `Intl.DateTimeFormat` to produce
 * locale-natural names. Defensive try/catch falls back to `en-US` for
 * malformed BCP-47 tags (e.g. `'not_a_locale'`) so a corrupt locale field
 * cannot crash the picker on every render.
 */

// Local fallback locale to avoid a circular import with locale-loader.js.
// Keep in sync with `LOCALE_DEFAULTS.locale` over there. en-US is used
// everywhere as the universal fallback.
const FALLBACK_LOCALE = 'en-US';
const FALLBACK_FIRST_DAY_OF_WEEK = 1;

/**
 * Construct a `Date` for `year`/`month`/`day` (1-12 / 1-31), forcing the
 * full year via `setFullYear` so years 0..99 don't decay into 1900..1999.
 *
 * @param {number} year
 * @param {number} month - 1..12
 * @param {number} day - 1..31
 * @returns {Date}
 */
function makeDate(year, month, day) {
  const d = new Date(year, month - 1, day);
  d.setFullYear(year);
  return d;
}

/**
 * Days in `month` of `year` (handles leap years).
 *
 * @param {number} year
 * @param {number} month - 1..12
 * @returns {number}
 */
function daysInMonth(year, month) {
  // Day 0 of next month = last day of this month.
  const d = new Date(year, month, 0);
  d.setFullYear(year);
  return d.getDate();
}

/**
 * Build the 6-row × 7-column day grid for `year`/`month`.
 *
 * Always 6 rows so the picker doesn't reflow when the month starts late or
 * ends early. Cells before the first-of-month or after the last-of-month
 * carry `otherMonth: true` so callers can render them dimmed.
 *
 * Year discipline: every `new Date(...)` here uses `makeDate` which calls
 * `setFullYear`, protecting against the JS year-trap (`new Date(99, ...)` →
 * 1999). Defensive — production paths only feed 4-digit years.
 *
 * @param {number} year - Full year (e.g. 2024). Years 0..99 are honored verbatim via setFullYear.
 * @param {number} month - 1..12.
 * @param {0 | 1} firstDayOfWeek - 0 = Sunday, 1 = Monday.
 * @returns {{ date: Date, otherMonth: boolean }[][]} 6×7 grid.
 */
export function getMonthGrid(year, month, firstDayOfWeek) {
  const firstDow = firstDayOfWeek === 0 ? 0 : 1; // normalize defensive
  // Day-of-week of the 1st (0=Sun..6=Sat). Shift so firstDow becomes column 0.
  const first = makeDate(year, month, 1);
  const firstWeekday = first.getDay();
  const leading = (firstWeekday - firstDow + 7) % 7;

  const totalCells = 42; // 6 weeks
  /** @type {{ date: Date, otherMonth: boolean }[][]} */
  const rows = [];
  let row = [];

  // Days from previous month for the leading slots.
  const prevMonth = month === 1 ? 12 : month - 1;
  const prevYear = month === 1 ? year - 1 : year;
  const prevMonthDays = daysInMonth(prevYear, prevMonth);
  for (let i = 0; i < leading; i += 1) {
    const day = prevMonthDays - leading + 1 + i;
    row.push({ date: makeDate(prevYear, prevMonth, day), otherMonth: true });
    if (row.length === 7) { rows.push(row); row = []; }
  }

  // Days of this month.
  const thisMonthDays = daysInMonth(year, month);
  for (let day = 1; day <= thisMonthDays; day += 1) {
    row.push({ date: makeDate(year, month, day), otherMonth: false });
    if (row.length === 7) { rows.push(row); row = []; }
  }

  // Trailing days from next month to fill out 42 cells.
  const nextMonth = month === 12 ? 1 : month + 1;
  const nextYear = month === 12 ? year + 1 : year;
  let nextDay = 1;
  const filled = leading + thisMonthDays;
  for (let i = filled; i < totalCells; i += 1) {
    row.push({ date: makeDate(nextYear, nextMonth, nextDay), otherMonth: true });
    nextDay += 1;
    if (row.length === 7) { rows.push(row); row = []; }
  }

  return rows;
}

/**
 * Try to construct an `Intl.DateTimeFormat` for `locale`; on `RangeError`
 * (malformed BCP-47 tag) or any other failure, fall back to `en-US`.
 *
 * @param {string} locale
 * @param {Intl.DateTimeFormatOptions} options
 * @returns {Intl.DateTimeFormat}
 */
function safeFormatter(locale, options) {
  try {
    return new Intl.DateTimeFormat(locale, options);
  } catch {
    return new Intl.DateTimeFormat(FALLBACK_LOCALE, options);
  }
}

/**
 * Locale-natural name of the month for `date` (e.g. "marzec", "March").
 * Includes the year in the typical "Month YYYY" form for picker headers.
 *
 * @param {Date} date
 * @param {string} locale - BCP-47 tag. Malformed tags fall back to en-US.
 * @returns {string}
 */
export function getMonthName(date, locale) {
  return safeFormatter(locale, { month: 'long', year: 'numeric' }).format(date);
}

/**
 * Locale-natural "long" full-date form (e.g. "15 March 2024" / "15 marca 2024").
 *
 * Behavior:
 *   - Malformed locale → falls back to en-US via `safeFormatter`.
 *   - `date` must be a valid `Date`. Passing an Invalid Date is a caller
 *     contract violation and may throw `RangeError: Invalid time value`
 *     from `Intl.DateTimeFormat.format`.
 *
 * @param {Date} date
 * @param {string} locale - BCP-47 tag. Malformed tags fall back to en-US.
 * @returns {string}
 */
export function formatFullDate(date, locale) {
  return safeFormatter(locale, { dateStyle: 'long' }).format(date);
}

/**
 * Short weekday names ordered to start with `firstDayOfWeek`. Returns an
 * array of length 7 (e.g. ["Mon","Tue",...,"Sun"] for firstDayOfWeek=1).
 *
 * Defensive: invalid `firstDayOfWeek` (anything other than 0 or 1) falls
 * back to 1 (Monday) since this helper is exported from `core/` and may
 * be called outside a `LocaleConfig`-validated runtime path.
 *
 * @param {0 | 1 | number} firstDayOfWeek - 0 = Sunday, 1 = Monday.
 * @param {string} locale - BCP-47 tag. Malformed tags fall back to en-US.
 * @returns {string[]} Length 7.
 */
export function getWeekdayHeaders(firstDayOfWeek, locale) {
  const firstDow = firstDayOfWeek === 0 || firstDayOfWeek === 1
    ? firstDayOfWeek
    : FALLBACK_FIRST_DAY_OF_WEEK;
  const fmt = safeFormatter(locale, { weekday: 'short' });
  // 2024-01-07 is a Sunday — anchor against a known Sunday and shift.
  const headers = new Array(7);
  for (let i = 0; i < 7; i += 1) {
    const dow = (firstDow + i) % 7; // 0..6, where 0 = Sunday
    const sample = makeDate(2024, 1, 7 + dow); // 7=Sun, 8=Mon, ...
    headers[i] = fmt.format(sample);
  }
  return headers;
}
